/**
 * Color helpers for values that come from USER DATA, not from the theme:
 * the hue a person picked for a collection or a card, whatever notation the
 * picker stored it in. Theme colors never pass through here — those are
 * tokens, resolved by CSS (src/css/themes.css) or read from the palette
 * (src/config/themes.js).
 */

import { IDENTITY_GLYPH_COLORS, IDENTITY_HUES } from '../config/brand';

export const COLOR_WHITE = '#fff';
export const FONT_COLOR_DARK = '#111';

const clampChannel = value => Math.max(0, Math.min(255, Math.round(value)));

const parseHex = (color) => {
	const match = color.match(/^#?([0-9a-f]{3,8})$/i);
	if (!match) return null;
	let hex = match[1];
	// #rgb and #rgba expand to their doubled form; the alpha byte is parsed but
	// not carried, since callers supply their own alpha.
	if (hex.length === 3 || hex.length === 4) {
		hex = hex.split('').map(ch => ch + ch).join('');
	}
	if (hex.length !== 6 && hex.length !== 8) return null;
	const intVal = parseInt(hex.slice(0, 6), 16);
	if (Number.isNaN(intVal)) return null;
	return {
		r: (intVal >> 16) & 255,
		g: (intVal >> 8) & 255,
		b: intVal & 255,
	};
};

const parseRgb = (color) => {
	const match = color.match(/^rgba?\(([^)]+)\)$/i);
	if (!match) return null;
	const parts = match[1].split(/[\s,/]+/).filter(Boolean);
	if (parts.length < 3) return null;
	const channel = (raw) => {
		const value = parseFloat(raw);
		if (Number.isNaN(value)) return null;
		return clampChannel(raw.includes('%') ? (value / 100) * 255 : value);
	};
	const [r, g, b] = parts.slice(0, 3).map(channel);
	if (r === null || g === null || b === null) return null;
	return { r, g, b };
};

const parseHsl = (color) => {
	const match = color.match(/^hsla?\(([^)]+)\)$/i);
	if (!match) return null;
	const parts = match[1].split(/[\s,/]+/).filter(Boolean);
	if (parts.length < 3) return null;
	const h = ((parseFloat(parts[0]) % 360) + 360) % 360;
	const s = parseFloat(parts[1]) / 100;
	const l = parseFloat(parts[2]) / 100;
	if ([h, s, l].some(Number.isNaN)) return null;
	const c = (1 - Math.abs((2 * l) - 1)) * s;
	const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
	const m = l - (c / 2);
	const sector = Math.floor(h / 60) % 6;
	const [r1, g1, b1] = [
		[c, x, 0], [x, c, 0], [0, c, x], [0, x, c], [x, 0, c], [c, 0, x],
	][sector];
	return {
		r: clampChannel((r1 + m) * 255),
		g: clampChannel((g1 + m) * 255),
		b: clampChannel((b1 + m) * 255),
	};
};

/**
 * Any CSS color a user's picker might store -> {r, g, b}, or null.
 *
 * Colors reach us in whatever notation they were saved in (the picker has
 * emitted both `#04bca5` and `rgb(255,157,0)` over time), so anything deriving
 * a tint or a contrast decision has to accept all of them — a parser that only
 * handled hex silently fell back to a neutral gray for the rest.
 */
export const parseColor = (color) => {
	if (!color || typeof color !== 'string') return null;
	const value = color.trim();
	return parseHex(value) || parseRgb(value) || parseHsl(value);
};

/** `color` at the given alpha, as an rgba() string. Null if unparseable. */
export const toRgba = (color, alpha = 0.12) => {
	const rgb = parseColor(color);
	if (!rgb) return null;
	return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

const channelLuminance = value => {
	const c = value / 255;
	return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

/** WCAG relative luminance of {r,g,b}, 0 (black) to 1 (white). */
const relativeLuminance = rgb => (0.2126 * channelLuminance(rgb.r)) + (0.7152 * channelLuminance(rgb.g)) + (0.0722 * channelLuminance(rgb.b));

// Picks a foreground that stays legible on top of `color`, so user-chosen
// swatches work whether they pick near-black or near-white.
export const readableTextColor = (color, { light = COLOR_WHITE, dark = FONT_COLOR_DARK } = {}) => {
	const rgb = parseColor(color);
	if (!rgb) return light;
	return relativeLuminance(rgb) > 0.45 ? dark : light;
};

const hexKey = (color) => {
	const rgb = parseColor(color);
	return rgb ? `${rgb.r},${rgb.g},${rgb.b}` : null;
};

// Built once: the palette is a constant, and every record tile asks.
const IDENTITY_GLYPH_BY_RGB = new Map(
	IDENTITY_HUES.map(hue => [hexKey(hue.value), IDENTITY_GLYPH_COLORS[hue.glyph]])
);

/**
 * The glyph color for an icon sitting on an identity color: the swatch's
 * DECLARED glyph when the color is one of the identity hues (in any notation
 * or case it was stored in), else the palette's own rule applied to the
 * custom color: white wherever white reaches 3:1, otherwise the dark glyph.
 *
 * Not readableTextColor for a custom color: its 0.45 luminance cut draws white
 * on everything darker than that, which is 2.1:1 to 3:1 on a mid-tone such as
 * #999999 (KPR-314: the custom picker's preview shows exactly this glyph, so it
 * has to be a legible one). Only colors between luminance 0.3 and 0.45 change,
 * and always to the glyph that reads better. A color that cannot be read at all
 * keeps the light glyph, as before.
 */
export const identityGlyphColor = (color) => {
	const key = hexKey(color);
	const declared = key ? IDENTITY_GLYPH_BY_RGB.get(key) : null;
	if (declared) return declared;
	const rgb = parseColor(color);
	if (!rgb) return COLOR_WHITE;
	// 1.05 / (L + 0.05) >= 3  <=>  L <= 0.3
	return (1.05 / (relativeLuminance(rgb) + 0.05)) >= 3 ? COLOR_WHITE : FONT_COLOR_DARK;
};
